import {CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {
  Alert,
  Badge,
  Button,
  Form,
  Spinner,
  ButtonGroup,
  Dropdown,
  DropdownButton,
} from 'react-bootstrap'
import Select from 'react-select'
import axios from 'axios'
import {useDisclourse} from '../../hooks/useDisclourse'
import {DrawerAddLead} from './components/DrawerAddLead'
import {IEdgesStagePipe, IFilterStage, IInfoLead, UpdatedLeadNode} from './leadTypes'
import {DrawerEditLead} from './components/DrawerEditLead'
import {useApolloClient, useQuery} from '@apollo/client'
import {GET_ALL_PIPELINES} from '../../gql/queries/pipelines'
import {GET_ALL_ORGANIZATIONS} from '../../gql/queries/organizationsQuery'
import {IResPipelines, IStagePipelineSet} from '../../types/pipelines'
import {LoadingLeadsPage} from './components/LoadingLeadsPage'
import {toast} from 'react-toastify'
import {ContentDragDropLeads} from './components/ContentDragDropLeads'
import {
  KANBAN_ZOOM_MAX,
  KANBAN_ZOOM_MIN,
  KANBAN_ZOOM_STEP,
  clampKanbanZoom,
  getStoredKanbanZoom,
  setStoredKanbanZoom,
} from './components/kanbanZoom'
import {useGetUser} from '../../store/userStore'
import {hasPermission, parseId} from '../../helpers'
import {ALL_PERMISSIONS, STORAGE_NAMES} from '../../helpers/consts'
import {ContentListLeads} from './components/ContentListLeads'
import {DateInput} from '../../components/DateInput'
import {IResAllListUsers} from '../../types/users'
import {GET_ALL_USERS_LIST} from '../../gql/queries/usersQuery'
import {ModalReasingLeads} from './components/ModalReasingLeads'
import {MultiSelectWithActions} from '../../components/MultiSelectWithActions'
import {ModalEditPipeline} from '../pipelines/components/ModalEditPipeline'
import {LeadTagManager} from './components/LeadTagManager'
import {ModalImportLeads} from './components/ModalImportLeads'
import {URL as BACKEND_URL, fetchNewToken} from '../../gql/client'
import {useTelephony} from '../../hooks/useTelephony'
import {TelephonyDialQueueResponse, TelephonyDialQueueSummary} from '../../types/telephony'
import {useSearchParams} from 'react-router-dom'

export const STATUS_LEADS = {
  Declined: 'Declined',
  Sold: 'Sold',
}

export const SEMAFORO_LIMITS = {
  red: 5,
  green: 3,
}

export const getClassTraffic = (dateLead: Date) => {
  const ahora = new Date()
  const diferenciaMs = ahora.getTime() - dateLead.getTime()
  const dias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24))
  if (dias >= SEMAFORO_LIMITS.red) {
    return {
      class: 'traffic-red',
      title: 'This element has ' + SEMAFORO_LIMITS.red + ' days old',
      diff: dias,
    }
  }
  if (dias >= SEMAFORO_LIMITS.green && dias < SEMAFORO_LIMITS.red) {
    return {
      class: 'traffic-orange',
      title: 'This element has ' + SEMAFORO_LIMITS.green + ' days old',
      diff: dias,
    }
  }
  return {
    class: 'traffic-green',
    title: 'This element has ' + SEMAFORO_LIMITS.green + ' days old',
    diff: dias,
  }
}

// Helper function to get default date range (3 months ago to tomorrow)
const getDefaultDateRange = () => {
  const today = new Date()

  // Date FROM: First day of 3 months ago at 00:00:00
  const dateFrom = new Date(today.getFullYear(), today.getMonth() - 3, 1)
  dateFrom.setHours(0, 0, 0, 0)

  // Date TO: Tomorrow at 23:59:59.999
  const dateTo = new Date(today)
  dateTo.setDate(dateTo.getDate() + 1)
  dateTo.setHours(23, 59, 59, 999)

  return {dateFrom, dateTo}
}

// Helper function to format date to YYYY-MM-DD
const formatDate = (date: Date | null) => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Helper functions for localStorage
const LEADS_STORAGE_KEY = 'leads-last-pipeline'
const LEADS_STORAGE_USER_KEY = 'leads-last-user-id'
const LEADS_VIEW_STORAGE_KEY = 'leads-view-mode'

const getPipelineStorageKey = (userId: string | null) =>
  userId ? `${LEADS_STORAGE_KEY}:${userId}` : LEADS_STORAGE_KEY

const getStoredPipelineId = (userId: string | null): string | null => {
  try {
    return window.localStorage.getItem(getPipelineStorageKey(userId))
  } catch {
    return null
  }
}

const setStoredPipelineId = (userId: string | null, pipelineId: string) => {
  try {
    window.localStorage.setItem(getPipelineStorageKey(userId), pipelineId)
  } catch {
    // Fail silently in incognito mode
  }
}

const getStoredUserId = (): string | null => {
  try {
    return window.localStorage.getItem(LEADS_STORAGE_USER_KEY)
  } catch {
    return null
  }
}

const setStoredUserId = (userId: string) => {
  try {
    window.localStorage.setItem(LEADS_STORAGE_USER_KEY, userId)
  } catch {
    // Fail silently in incognito mode
  }
}

const getStoredLeadsView = (): 'card' | 'list' => {
  try {
    const storedView = window.localStorage.getItem(LEADS_VIEW_STORAGE_KEY)
    return storedView === 'list' ? 'list' : 'card'
  } catch {
    return 'card'
  }
}

const setStoredLeadsView = (view: 'card' | 'list') => {
  try {
    window.localStorage.setItem(LEADS_VIEW_STORAGE_KEY, view)
  } catch {
    // Fail silently in incognito mode
  }
}

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

const authGet = async <T,>(url: string) => {
  try {
    return await axios.get<T>(url, {headers: getAuthHeaders()})
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        return await axios.get<T>(url, {
          headers: {Authorization: `Bearer ${newToken}`},
        })
      }
    }
    throw error
  }
}

const authPost = async <T,>(url: string, data: Record<string, unknown>) => {
  try {
    return await axios.post<T>(url, data, {headers: getAuthHeaders()})
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        return await axios.post<T>(url, data, {
          headers: {Authorization: `Bearer ${newToken}`},
        })
      }
    }
    throw error
  }
}

const colon = ':'
const bullet = ' • '
const space = ' '
const midDot = ' · '

const lpM = defineMessages({
  allOrganizations: {id: 'leads.page.allOrganizations', defaultMessage: 'All Organizations'},
  selectOrganization: {id: 'leads.page.selectOrganization', defaultMessage: 'Select Organization'},
  selectPipeline: {id: 'leads.page.selectPipeline', defaultMessage: 'Select Pipeline'},
  searchStages: {id: 'leads.page.searchStages', defaultMessage: 'Search stages'},
  viewAll: {id: 'leads.page.viewAll', defaultMessage: 'View all'},
  viewSoldDeclined: {id: 'leads.page.viewSoldDeclined', defaultMessage: 'View Sold & Declined'},
  selectStage: {id: 'leads.page.selectStage', defaultMessage: 'Select stage'},
  viewAllStage: {id: 'leads.page.viewAllStage', defaultMessage: 'View all stage'},
  dateFrom: {id: 'leads.page.dateFrom', defaultMessage: 'Date From'},
  dateTo: {id: 'leads.page.dateTo', defaultMessage: 'Date To'},
  myLeads: {id: 'leads.page.myLeads', defaultMessage: 'My Leads'},
  tagManager: {id: 'leads.page.tagManager', defaultMessage: 'Tag Manager'},
  unassignedLeads: {id: 'leads.page.unassignedLeads', defaultMessage: 'Unassigned Leads'},
  configurePipeline: {
    id: 'leads.page.configurePipeline',
    defaultMessage: 'Configure pipeline',
  },
  searchNamePhone: {id: 'leads.page.searchNamePhone', defaultMessage: 'Name, email or phone...'},
  selectUsers: {id: 'leads.page.selectUsers', defaultMessage: 'Select user(s)...'},
  zoomOut: {id: 'leads.page.zoomOut', defaultMessage: 'Make the stages smaller'},
  zoomIn: {id: 'leads.page.zoomIn', defaultMessage: 'Make the stages bigger'},
  zoomReset: {id: 'leads.page.zoomReset', defaultMessage: 'Reset the stages size'},
  dialTitle: {id: 'leads.page.dial.title', defaultMessage: 'PolicyDial Auto Dial Queue'},
  dialCampaign: {id: 'leads.page.dial.campaign', defaultMessage: 'Campaign {id}'},
  dialStatsSelected: {
    id: 'leads.page.dial.statsSelected',
    defaultMessage: '{completed} completed / {total} selected',
  },
  dialStatsPending: {id: 'leads.page.dial.statsPending', defaultMessage: '{count} pending'},
  dialStatsDialing: {id: 'leads.page.dial.statsDialing', defaultMessage: '{count} dialing'},
  dialStatsFailed: {id: 'leads.page.dial.statsFailed', defaultMessage: ' • {count} failed'},
  dialCurrent: {id: 'leads.page.dial.current', defaultMessage: 'Current'},
  dialNextCall: {id: 'leads.page.dial.nextCall', defaultMessage: 'Next call scheduled for {time}.'},
  dialPause: {id: 'leads.page.dial.pause', defaultMessage: 'Pause'},
  dialResume: {id: 'leads.page.dial.resume', defaultMessage: 'Resume'},
  dialStop: {id: 'leads.page.dial.stop', defaultMessage: 'Stop'},
  dialStarting: {id: 'leads.page.dial.starting', defaultMessage: 'Starting...'},
  dialStart: {id: 'leads.page.dial.start', defaultMessage: 'Start Auto Dial Queue ({count})'},
  connectHint: {
    id: 'leads.page.dial.connectHint',
    defaultMessage: 'Connect PolicyDial and keep an active session before starting the queue.',
  },
  toastNotEnabled: {
    id: 'leads.page.toast.notEnabled',
    defaultMessage: 'PolicyDial auto dial queue is not enabled for this account.',
  },
  toastNeedSession: {
    id: 'leads.page.toast.needSession',
    defaultMessage: 'PolicyDial must have an active session before starting the auto dial queue.',
  },
  toastSelectLead: {
    id: 'leads.page.toast.selectLead',
    defaultMessage: 'Select at least one visible lead before starting the queue.',
  },
  toastStarted: {
    id: 'leads.page.toast.started',
    defaultMessage: 'Auto dial queue started in PolicyDial.',
  },
  toastCreated: {
    id: 'leads.page.toast.created',
    defaultMessage: 'Auto dial queue created and waiting for the next call.',
  },
  toastPaused: {id: 'leads.page.toast.paused', defaultMessage: 'Auto dial queue paused.'},
  toastResumed: {id: 'leads.page.toast.resumed', defaultMessage: 'Auto dial queue resumed.'},
  toastResumedInProgress: {
    id: 'leads.page.toast.resumedInProgress',
    defaultMessage: 'Auto dial queue resumed. The current call remains in progress.',
  },
  toastStopped: {id: 'leads.page.toast.stopped', defaultMessage: 'Auto dial queue stopped.'},
  toastUnableLoad: {
    id: 'leads.page.toast.unableLoad',
    defaultMessage: 'Unable to load the auto dial queue.',
  },
  toastUnableStart: {
    id: 'leads.page.toast.unableStart',
    defaultMessage: 'Unable to start the auto dial queue.',
  },
  toastUnablePause: {
    id: 'leads.page.toast.unablePause',
    defaultMessage: 'Unable to pause the auto dial queue.',
  },
  toastUnableResume: {
    id: 'leads.page.toast.unableResume',
    defaultMessage: 'Unable to resume the auto dial queue.',
  },
  toastUnableStop: {
    id: 'leads.page.toast.unableStop',
    defaultMessage: 'Unable to stop the auto dial queue.',
  },
})

export const LeadsPage = () => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage(
      {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
      {message}
    )
  const apolloClient = useApolloClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useGetUser((s) => s.user)
  const permissions = useGetUser((s) => s.user?.permissions)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const [organizationId, setOrganizationId] = useState<string>('')
  const telephony = useTelephony()
  const isRingIntegration = localStorage.getItem(STORAGE_NAMES.RING_CENTRAL) ? true : false
  const currentUserId = user?.user?.id ? String(user.user.id) : null
  const currentUserGroup = user?.user?.primaryGroup?.name || ''

  // Only fetch users list if user has export permission (Admins only)
  const canExportLeads = permissions
    ? hasPermission(permissions, ALL_PERMISSIONS.EXPORT_LEAD)
    : false
  const canImportLeads = permissions
    ? hasPermission(permissions, ALL_PERMISSIONS.IMPORT_LEAD)
    : false
  const canAddLeads = permissions
    ? hasPermission(permissions, ALL_PERMISSIONS.ADD_LEAD)
    : false
  // Asignar revisores/grupos ya no cuelga de export_lead: son cosas distintas y
  // el backend las exige con change_pipeline.
  const canEditPipeline = permissions
    ? hasPermission(permissions, ALL_PERMISSIONS.EDIT_PIPELINE)
    : false
  const defaultsToMyLeads = ['Agente'].includes(currentUserGroup)
  const canUsePolicyDialQueue = Boolean(currentUserId && telephony.config.vicidial_enabled)

  const {
    data: dataPipelines,
    loading: loadingPipelines,
    error: errorPipelines,
  } = useQuery<IResPipelines>(GET_ALL_PIPELINES, {
    variables: {
      organization: isSuperAdmin && organizationId ? parseId(organizationId) : null,
    },
    fetchPolicy: 'cache-first', // Use cache for pipelines (rarely change)
  })

  const {data: dataOrganizations, loading: loadingOrganizations} = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
    skip: !isSuperAdmin,
  })

  const {data: dataMembers, error: errorMembers} = useQuery<IResAllListUsers>(GET_ALL_USERS_LIST, {
    fetchPolicy: 'cache-first', // Use cache for users list (rarely changes)
    skip: !canExportLeads, // Skip query if user doesn't have export permission
  })

  const [pipelineId, setPipelineId] = useState(() => getStoredPipelineId(currentUserId) || '')

  const [flag, setFlag] = useState(0)
  const [pageRefresh, setPageRefresh] = useState(0)
  const [leadPatch, setLeadPatch] = useState<UpdatedLeadNode | null>(null)

  const [flagView, setFlagView] = useState<'card' | 'list'>(() => getStoredLeadsView())
  const [kanbanZoom, setKanbanZoom] = useState<number>(() => getStoredKanbanZoom())

  const changeKanbanZoom = useCallback((next: number) => {
    const zoom = clampKanbanZoom(next)
    setKanbanZoom(zoom)
    setStoredKanbanZoom(zoom)
  }, [])

  const [isLoadingChangeState, setIsLoadingChangeState] = useState(false)
  const [isExport, setIsExport] = useState(false)
  const [showTagManager, setShowTagManager] = useState(false)

  const [infoLead, setInfoLead] = useState<null | IInfoLead>(null)
  const [infoReAssign, setInfoReAssign] = useState<null | {
    fromUserId: string
    fromUserName: string
    pipelineId: string
    pipelineName: string
    statePipeline: string
    stateNamePipeline: string
    searchName: string
    searchPhone: string
    dateFrom: Date | null
    dateTo: Date | null
    assignType: string
    leadsSelected?: {id: string; name: string; stage: string; user: string}[]
  }>(null)

  const [leadsSelected, setLeadSelected] = useState<
    {id: string; name: string; stage: string; user: string}[] | undefined
  >([])

  const [stageByPipeline, setStageByPipeline] = useState<IStagePipelineSet[] | null>(null)

  // Un solo modal de configuracion del pipeline reemplaza los antiguos
  // "Asignar revisores" / "Asignar grupos": esas dos pantallas ahora son
  // pestanas dentro de ModalEditPipeline.
  const [infoConfigPipeline, setInfoConfigPipeline] = useState<null | {
    id: string
    name: string
  }>(null)
  const {isOpen: isOpenEdit, onClose: onCloseEdit, onOpen: onOpenEdit} = useDisclourse()
  const {isOpen: isOpenAdd, onClose: onCloseAdd, onOpen: onOpenAdd} = useDisclourse()
  const {isOpen: isOpenReAssign, onClose: onCloseReAssign, onOpen: onOpenReAssign} = useDisclourse()
  const {
    isOpen: isOpenImport,
    onClose: onCloseImport,
    onOpen: onOpenImport,
  } = useDisclourse()
  const {
    isOpen: isOpenConfigPipeline,
    onClose: onCloseConfigPipeline,
    onOpen: onOpenConfigPipeline,
  } = useDisclourse()
  const [filterStage, setFilterStage] = useState<IFilterStage>('all')
  const [statePipeline, setStagePipelineId] = useState('')
  const [searchName, setSearchName] = useState('')
  const [searchPhone, setSearchPhone] = useState('')
  const [searchText, setSearchText] = useState('')
  const [valueUsers, setValueUsers] = useState<string[]>([])
  const [filterUnassigned, setFilterUnassigned] = useState(false)
  const [didInitMyLeads, setDidInitMyLeads] = useState(false)
  const [stagesDeclined, setStagesDeclined] = useState<IEdgesStagePipe | null>(null)
  const [dialQueue, setDialQueue] = useState<TelephonyDialQueueSummary | null>(null)
  const [loadingDialQueue, setLoadingDialQueue] = useState(false)
  const [mutatingDialQueue, setMutatingDialQueue] = useState(false)
  const lastDialQueueIdRef = useRef<number | null>(null)
  const openedLeadDeepLinkRef = useRef<string | null>(null)
  const leadDeepLinkPipelineId = searchParams.get('pipelineId')
  const leadDeepLinkLeadId = searchParams.get('leadId')

  // Initialize with default date range (3 months ago to tomorrow) — first day of that month
  const [dateFrom, setDateFrom] = useState<Date | null>(() => getDefaultDateRange().dateFrom)
  const [dateTo, setDateTo] = useState<Date | null>(() => getDefaultDateRange().dateTo)

  const stagesBySelectedPipeline = useMemo(() => {
    if (!dataPipelines || !pipelineId) return null
    const selectedPipeline = dataPipelines.allPipelines.edges.find(
      ({node}) => node.id === pipelineId
    )
    return selectedPipeline?.node.stagePipelineSet?.edges ?? null
  }, [dataPipelines, pipelineId])

  const isMyLeadsActive = Boolean(
    currentUserId && valueUsers.length === 1 && valueUsers[0] === currentUserId
  )
  const hasActivePolicyDialSession = Boolean(
    canUsePolicyDialQueue && telephony.vicidialSession.activeSession
  )
  const effectiveQueueCampaignId = useMemo(
    () =>
      String(
        telephony.vicidialSession.selectedCampaignId ||
          telephony.vicidialSession.campaignId ||
          telephony.config.vicidial_campaign_id ||
          ''
      ).trim(),
    [
      telephony.config.vicidial_campaign_id,
      telephony.vicidialSession.campaignId,
      telephony.vicidialSession.selectedCampaignId,
    ]
  )
  const selectedLeadCount = leadsSelected?.length || 0
  const showStartDialQueueButton =
    flagView === 'list' && canUsePolicyDialQueue && selectedLeadCount > 0 && !dialQueue
  const disableStartDialQueue = mutatingDialQueue || !hasActivePolicyDialSession

  const loadDialQueue = useCallback(
    async (silent = false) => {
      if (!canUsePolicyDialQueue) {
        setDialQueue(null)
        return null
      }

      if (!silent) {
        setLoadingDialQueue(true)
      }

      try {
        const response = await authGet<TelephonyDialQueueResponse>(
          `${BACKEND_URL}/api/telephony/dial-queue/current`
        )
        const nextQueue = response.data.queue || null
        setDialQueue(nextQueue)
        return nextQueue
      } catch (error: any) {
        const message =
          error?.response?.data?.error || error?.message || intl.formatMessage(lpM.toastUnableLoad)
        if (!silent) {
          toast.error(message)
        }
        return null
      } finally {
        if (!silent) {
          setLoadingDialQueue(false)
        }
      }
    },
    [canUsePolicyDialQueue]
  )

  const refreshList = useCallback(() => {
    setPageRefresh((p) => p + 1)
  }, [])

  const didMountOrgFilterRef = useRef(false)
  useEffect(() => {
    if (!didMountOrgFilterRef.current) {
      didMountOrgFilterRef.current = true
      return
    }
    setPipelineId('')
  }, [organizationId])

  useEffect(() => {
    if (!currentUserId) return
    const lastUserId = getStoredUserId()
    if (lastUserId && lastUserId !== currentUserId) {
      apolloClient.cache.evict({fieldName: 'allPipelines'})
      apolloClient.cache.gc()
      setPipelineId('')
    }
    setStoredUserId(currentUserId)
    const storedPipelineId = getStoredPipelineId(currentUserId)
    if (storedPipelineId) {
      setPipelineId(storedPipelineId)
    }
  }, [apolloClient, currentUserId])

  useEffect(() => {
    if (didInitMyLeads || !currentUserId) return
    if (defaultsToMyLeads) {
      setValueUsers([currentUserId])
    }
    setDidInitMyLeads(true)
  }, [currentUserId, defaultsToMyLeads, didInitMyLeads])

  useEffect(() => {
    if (!pipelineId) return
    if (flagView === 'list') {
      setStagePipelineId('')
      refreshList()
    }
    setLeadsSelected([])
  }, [pipelineId, flagView])

  useEffect(() => {
    if (!dataPipelines) return
    if (dataPipelines.allPipelines.edges.length > 0) {
      if (leadDeepLinkPipelineId) {
        const pipelineFromUrl = dataPipelines.allPipelines.edges.find(
          ({node}) =>
            node.id === leadDeepLinkPipelineId ||
            String(parseId(node.id)) === String(leadDeepLinkPipelineId)
        )
        if (pipelineFromUrl) {
          setFlagView('card')
          setPipelineId(pipelineFromUrl.node.id)
          setStoredPipelineId(currentUserId, pipelineFromUrl.node.id)
          return
        }
      }

      const storedId = getStoredPipelineId(currentUserId)
      // Check if stored pipeline exists in available pipelines
      const storedPipelineExists =
        storedId && dataPipelines.allPipelines.edges.some((edge) => edge.node.id === storedId)

      if (storedPipelineExists) {
        setPipelineId(storedId)
      } else {
        // Fallback to first pipeline if stored one doesn't exist
        const firstPipelineId = dataPipelines.allPipelines.edges[0].node.id
        setPipelineId(firstPipelineId)
        setStoredPipelineId(currentUserId, firstPipelineId)
      }
    }
  }, [currentUserId, dataPipelines, leadDeepLinkPipelineId])

  useEffect(() => {
    if (!leadDeepLinkLeadId) return

    const deepLinkKey = `${leadDeepLinkPipelineId || ''}:${leadDeepLinkLeadId}`
    if (openedLeadDeepLinkRef.current === deepLinkKey) return

    if (leadDeepLinkPipelineId) {
      if (!dataPipelines) return
      const pipelineFromUrl = dataPipelines.allPipelines.edges.find(
        ({node}) =>
          node.id === leadDeepLinkPipelineId ||
          String(parseId(node.id)) === String(leadDeepLinkPipelineId)
      )
      if (pipelineFromUrl && pipelineId !== pipelineFromUrl.node.id) {
        return
      }
    }

    setFlagView('card')
    setInfoLead({id: leadDeepLinkLeadId, name: searchParams.get('leadName') || 'Lead'})
    onOpenEdit()
    openedLeadDeepLinkRef.current = deepLinkKey
  }, [
    dataPipelines,
    leadDeepLinkLeadId,
    leadDeepLinkPipelineId,
    onOpenEdit,
    pipelineId,
    searchParams,
  ])

  useEffect(() => {
    if (!stagesBySelectedPipeline) {
      setStagesDeclined(null)
      setStageByPipeline(null)
      return
    }
    const declinedStage = stagesBySelectedPipeline.find(({node}) => node.stageType === 'DECLINED')
    setStagesDeclined(declinedStage ?? null)
    setStageByPipeline(stagesBySelectedPipeline)
    setLeadsSelected([])
  }, [stagesBySelectedPipeline])

  useEffect(() => {
    if (errorPipelines) toast.error(errMsg(errorPipelines.message))
  }, [errorPipelines])

  // Save selected pipeline to localStorage whenever it changes
  useEffect(() => {
    if (pipelineId) {
      setStoredPipelineId(currentUserId, pipelineId)
    }
  }, [currentUserId, pipelineId])

  useEffect(() => {
    setStoredLeadsView(flagView)
  }, [flagView])

  const pipelinesReady = !loadingPipelines && Boolean(dataPipelines)

  useEffect(() => {
    // Only poll while the agent has an active VICIdial session (not just the
    // feature enabled), and only after pipelines have finished loading so this
    // doesn't compete with the initial pipelines request.
    if (!hasActivePolicyDialSession || !pipelinesReady) return

    loadDialQueue()
    const intervalId = window.setInterval(() => {
      loadDialQueue(true)
    }, 5000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [hasActivePolicyDialSession, pipelinesReady, loadDialQueue])

  useEffect(() => {
    const currentQueueId = dialQueue?.id ?? null
    if (lastDialQueueIdRef.current && !currentQueueId) {
      refreshList()
    }
    lastDialQueueIdRef.current = currentQueueId
  }, [dialQueue, refreshList])

  const updateListLeads = () => {
    setLeadsSelected([])
    console.log('leadsSelected', leadsSelected)
    setFlag((p) => p + 1)
  }

  const handleCloseEditLead = () => {
    onCloseEdit()
    if (!leadDeepLinkLeadId) return

    openedLeadDeepLinkRef.current = null
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('leadId')
    nextParams.delete('leadName')
    setSearchParams(nextParams, {replace: true})
  }

  const setLeadsSelected = (leads: {id: string; name: string; stage: string; user: string}[]) => {
    setLeadSelected(leads)
  }

  // Se acepta cualquier formato de teléfono (con paréntesis, guiones, puntos o
  // código de país): el backend compara solo los dígitos.
  const isPhoneLike = (value: string) => {
    const trimmed = value.trim()
    return /^[\d\s().+\-/]+$/.test(trimmed) && trimmed.replace(/\D+/g, '').length >= 3
  }

  const applySearch = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed) {
      setSearchName('')
      setSearchPhone('')
    } else if (isPhoneLike(trimmed)) {
      setSearchPhone(trimmed)
      setSearchName('')
    } else {
      setSearchName(trimmed)
      setSearchPhone('')
    }
    updateListLeads()
  }

  const research = () => {
    applySearch(searchText)
  }

  const handleToggleMyLeads = () => {
    if (!currentUserId) return
    setLeadsSelected([])
    setFilterUnassigned(false)
    setValueUsers((current) =>
      current.length === 1 && current[0] === currentUserId ? [] : [currentUserId]
    )
  }

  const handleToggleUnassigned = () => {
    setLeadsSelected([])
    setFilterUnassigned((prev) => {
      if (!prev) setValueUsers([])
      return !prev
    })
  }

  const handleStartDialQueue = async () => {
    if (!canUsePolicyDialQueue) {
      toast.error(intl.formatMessage(lpM.toastNotEnabled))
      return
    }
    if (!hasActivePolicyDialSession) {
      toast.error(intl.formatMessage(lpM.toastNeedSession))
      return
    }

    const leadIds = (leadsSelected || [])
      .map((lead) => Number(parseId(lead.id)))
      .filter((leadId) => Number.isFinite(leadId) && leadId > 0)

    if (!leadIds.length) {
      toast.error(intl.formatMessage(lpM.toastSelectLead))
      return
    }

    setMutatingDialQueue(true)
    try {
      const response = await authPost<TelephonyDialQueueResponse>(
        `${BACKEND_URL}/api/telephony/dial-queue/start`,
        {
          provider: 'VICIDIAL',
          campaign_id: effectiveQueueCampaignId || undefined,
          lead_ids: leadIds,
        }
      )

      const nextQueue = response.data.queue || null
      setDialQueue(nextQueue)
      setLeadsSelected([])
      refreshList()

      if (response.data.started) {
        toast.success(intl.formatMessage(lpM.toastStarted))
      } else if (nextQueue?.last_error) {
        toast.warning(nextQueue.last_error)
      } else {
        toast.info(intl.formatMessage(lpM.toastCreated))
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.error || error?.message || intl.formatMessage(lpM.toastUnableStart)
      toast.error(message)
    } finally {
      setMutatingDialQueue(false)
    }
  }

  const handlePauseDialQueue = async () => {
    if (!dialQueue) return
    setMutatingDialQueue(true)
    try {
      const response = await authPost<TelephonyDialQueueResponse>(
        `${BACKEND_URL}/api/telephony/dial-queue/pause`,
        {queue_id: dialQueue.id}
      )
      setDialQueue(response.data.queue || null)
      toast.success(intl.formatMessage(lpM.toastPaused))
    } catch (error: any) {
      const message =
        error?.response?.data?.error || error?.message || intl.formatMessage(lpM.toastUnablePause)
      toast.error(message)
    } finally {
      setMutatingDialQueue(false)
    }
  }

  const handleResumeDialQueue = async () => {
    if (!dialQueue) return
    setMutatingDialQueue(true)
    try {
      const response = await authPost<TelephonyDialQueueResponse>(
        `${BACKEND_URL}/api/telephony/dial-queue/resume`,
        {
          queue_id: dialQueue.id,
          provider: 'VICIDIAL',
        }
      )
      const nextQueue = response.data.queue || null
      setDialQueue(nextQueue)
      if (response.data.started) {
        toast.success(intl.formatMessage(lpM.toastResumed))
      } else {
        toast.info(intl.formatMessage(lpM.toastResumedInProgress))
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.error || error?.message || intl.formatMessage(lpM.toastUnableResume)
      toast.error(message)
    } finally {
      setMutatingDialQueue(false)
    }
  }

  const handleStopDialQueue = async () => {
    if (!dialQueue) return
    setMutatingDialQueue(true)
    try {
      await authPost<TelephonyDialQueueResponse>(`${BACKEND_URL}/api/telephony/dial-queue/stop`, {
        queue_id: dialQueue.id,
      })
      setDialQueue(null)
      toast.success(intl.formatMessage(lpM.toastStopped))
    } catch (error: any) {
      const message =
        error?.response?.data?.error || error?.message || intl.formatMessage(lpM.toastUnableStop)
      toast.error(message)
    } finally {
      setMutatingDialQueue(false)
    }
  }

  useEffect(() => {
    if (errorMembers) toast.error(errMsg(errorMembers.message))
  }, [errorMembers])

  if (loadingPipelines || !dataPipelines || !permissions) {
    return <LoadingLeadsPage />
  }
  const isFilled = (value?: string | null) => value != null && value.trim() !== ''

  const userOptions =
    dataMembers?.allUsersList.map((node) => ({
      value: node.id,
      label: `${node.firstName} ${node.lastName} - ${node.email}`,
    })) ?? []

  const selectedUserOptions = userOptions.filter((o) => valueUsers.includes(o.value))
  const valueUser = valueUsers[0] ?? ''

  const isByFilters =
    isFilled(statePipeline) ||
    dateFrom != null ||
    dateTo != null ||
    valueUsers.length > 0 ||
    isFilled(searchName) ||
    isFilled(searchPhone)

  return (
    <>
      <div className='d-flex flex-column w-100'>
        {/* Row 1: Pipeline | Stage | Dates | → My Leads | Actions */}
        <div className='w-100 mb-2 d-flex align-items-center gap-2 flex-wrap'>
          {isSuperAdmin && (
            <Form.Select
              aria-label={intl.formatMessage(lpM.selectOrganization)}
              value={organizationId}
              style={{width: '220px'}}
              disabled={loadingOrganizations}
              onChange={(e) => setOrganizationId(e.target.value)}
            >
              <option value=''>
                {loadingOrganizations
                  ? intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})
                  : intl.formatMessage(lpM.allOrganizations)}
              </option>
              {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => (
                <option key={node.id} value={parseId(node.id)}>
                  {node.name}
                </option>
              ))}
            </Form.Select>
          )}

          <Form.Select
            aria-label={intl.formatMessage(lpM.selectPipeline)}
            value={pipelineId}
            style={{width: '220px'}}
            onChange={(e) => {
              const {value} = e.target
              if (value) setPipelineId(value)
            }}
          >
            <option value=''>{intl.formatMessage(lpM.selectPipeline)}</option>
            {dataPipelines.allPipelines.edges.map(({node}) => (
              <option key={node.id} value={node.id}>
                {node.name}
              </option>
            ))}
          </Form.Select>

          {flagView === 'card' ? (
            <Form.Select
              value={filterStage}
              aria-label={intl.formatMessage(lpM.searchStages)}
              style={{width: '220px'}}
              onChange={(e) => setFilterStage(e.target.value as IFilterStage)}
            >
              <option value='all'>{intl.formatMessage(lpM.viewAll)}</option>
              <option value='soldDecline'>{intl.formatMessage(lpM.viewSoldDeclined)}</option>
              <option value='others'>
                {intl.formatMessage({
                  id: 'leads.list.viewActiveStages',
                  defaultMessage: 'View active stages',
                })}
              </option>
            </Form.Select>
          ) : (
            <Form.Select
              aria-label={intl.formatMessage(lpM.selectStage)}
              style={{width: '220px'}}
              onChange={(e) => {
                const {value} = e.target
                setStagePipelineId(value)
                if (value) refreshList()
              }}
            >
              <option value=''>{intl.formatMessage(lpM.viewAllStage)}</option>
              {stagesBySelectedPipeline
                ?.slice()
                .sort((a, b) => a.node.order - b.node.order)
                .map(({node}) => (
                  <option key={node.id} value={node.id}>
                    {node.name}
                  </option>
                ))}
            </Form.Select>
          )}

          <div style={{width: '160px'}}>
            <DateInput
              id='dateFrom'
              name='dateFrom'
              placeholder={intl.formatMessage(lpM.dateFrom)}
              value={formatDate(dateFrom)}
              onChange={(_, value) => {
                if (!value) {
                  setDateFrom(null)
                  return
                }
                const date = new Date(`${value}T00:00:00`)
                date.setHours(0, 0, 0, 0)
                if (dateTo && date > dateTo) {
                  const newDateTo = new Date(date)
                  newDateTo.setHours(23, 59, 59, 999)
                  setDateTo(newDateTo)
                }
                setDateFrom(date)
              }}
            />
          </div>

          <div style={{width: '160px'}}>
            <DateInput
              id='dateTo'
              name='dateTo'
              placeholder={intl.formatMessage(lpM.dateTo)}
              value={formatDate(dateTo)}
              onChange={(_, value) => {
                if (!value) {
                  setDateTo(null)
                  return
                }
                const date = new Date(`${value}T00:00:00`)
                date.setHours(23, 59, 59, 999)
                if (dateFrom && date < dateFrom) {
                  const newDateFrom = new Date(date)
                  newDateFrom.setHours(0, 0, 0, 0)
                  setDateFrom(newDateFrom)
                }
                setDateTo(date)
              }}
            />
          </div>

          <div className='ms-auto d-flex align-items-center gap-2 flex-wrap'>
            {isLoadingChangeState && <Spinner animation='border' size='sm' />}
            <Button
              variant={isMyLeadsActive ? 'primary' : 'outline-primary'}
              onClick={handleToggleMyLeads}
              disabled={!currentUserId}
            >
              <i className='bi bi-person-check me-1'></i>
              {intl.formatMessage(lpM.myLeads)}
            </Button>

            <Button variant='outline-secondary' onClick={() => setShowTagManager(true)}>
              <i className='bi bi-tags me-1'></i>
              {intl.formatMessage(lpM.tagManager)}
            </Button>

            {permissions && (canExportLeads || canImportLeads || canAddLeads || canEditPipeline) ? (
              <DropdownButton
                id='dropdown-basic-button'
                title={intl.formatMessage({id: 'common.field.actions', defaultMessage: 'Actions'})}
                className='btn-actions-group'
              >
                {canAddLeads && (
                  <Dropdown.Item
                    className='px-4 mx-1 my-2 py-2 d-flex align-items-center gap-1'
                    onClick={onOpenAdd}
                  >
                    <i className='bi bi-person-vcard'></i>
                    <FormattedMessage id='leads.list.addLead' defaultMessage='Add lead' />
                  </Dropdown.Item>
                )}
                {canImportLeads && (
                  <Dropdown.Item
                    className='px-4 mx-1 my-2 py-2 d-flex align-items-center gap-1'
                    disabled={!pipelineId}
                    onClick={onOpenImport}
                  >
                    <i className='bi bi-upload'></i>
                    <FormattedMessage id='leads.import.action' defaultMessage='Import leads' />
                  </Dropdown.Item>
                )}
                {canExportLeads && <Dropdown.Item
                  onClick={() => {
                    const userNode = dataMembers?.allUsersList.find((node) => node.id === valueUser)
                    const stageSelectedNode = stageByPipeline?.find(
                      ({node}) => node.id === statePipeline
                    )?.node.name
                    const fullName = userNode ? `${userNode.firstName} ${userNode.lastName}` : ''
                    let assingType = 'by-user'
                    if (leadsSelected && leadsSelected.length > 0) {
                      assingType = 'by-leads-selected'
                    } else if (
                      (statePipeline != null && statePipeline !== '') ||
                      dateFrom != null ||
                      dateTo != null ||
                      (searchName != null && searchName !== '') ||
                      (searchPhone != null && searchPhone !== '')
                    )
                      assingType = 'by-filters'
                    const pipelineAux = dataPipelines?.allPipelines?.edges.find(
                      ({node}) => node.id === pipelineId
                    )
                    setIsExport(false)
                    setInfoReAssign({
                      fromUserId: valueUser,
                      fromUserName: fullName,
                      pipelineId: pipelineId,
                      pipelineName: pipelineAux?.node.name ?? '',
                      statePipeline: statePipeline,
                      stateNamePipeline: stageSelectedNode ?? '',
                      dateFrom: dateFrom,
                      dateTo: dateTo,
                      searchName: searchName,
                      searchPhone: searchPhone,
                      assignType: assingType,
                      leadsSelected: leadsSelected,
                    })
                    onOpenReAssign()
                  }}
                >
                  <i className='bi bi-person-up' />
                  <FormattedMessage id='leads.reassign.action' defaultMessage='Reassign leads' />
                </Dropdown.Item>}
                {canExportLeads && <Dropdown.Item onClick={handleToggleUnassigned} active={filterUnassigned}>
                  <i className='bi bi-person-dash me-1' />
                  {intl.formatMessage(lpM.unassignedLeads)}
                </Dropdown.Item>}
                {canEditPipeline && (
                  <Dropdown.Item
                    disabled={!pipelineId}
                    onClick={() => {
                      const pipelineAux = dataPipelines?.allPipelines?.edges.find(
                        ({node}) => node.id === pipelineId
                      )
                      if (!pipelineAux) return
                      setInfoConfigPipeline({
                        id: pipelineAux.node.id,
                        name: pipelineAux.node.name,
                      })
                      onOpenConfigPipeline()
                    }}
                  >
                    <i className='bi bi-sliders' />
                    {intl.formatMessage(lpM.configurePipeline)}
                  </Dropdown.Item>
                )}
                {canExportLeads && <Dropdown.Item
                  disabled={!isByFilters}
                  onClick={() => {
                    const userNode = dataMembers?.allUsersList.find((node) => node.id === valueUser)
                    const stageSelectedNode = stageByPipeline?.find(
                      ({node}) => node.id === statePipeline
                    )?.node.name
                    const fullName = userNode ? `${userNode.firstName} ${userNode.lastName}` : ''
                    const pipelineAux = dataPipelines?.allPipelines?.edges.find(
                      ({node}) => node.id === pipelineId
                    )
                    setIsExport(true)
                    setInfoReAssign({
                      fromUserId: valueUser,
                      fromUserName: fullName,
                      pipelineId: pipelineId,
                      pipelineName: pipelineAux?.node.name ?? '',
                      statePipeline: statePipeline,
                      stateNamePipeline: stageSelectedNode ?? '',
                      dateFrom: dateFrom,
                      dateTo: dateTo,
                      searchName: searchName,
                      searchPhone: searchPhone,
                      assignType: '',
                      leadsSelected: leadsSelected,
                    })
                    onOpenReAssign()
                  }}
                >
                  <i className='bi bi-download'></i>
                  <FormattedMessage id='leads.reassign.export' defaultMessage='Export leads' />
                </Dropdown.Item>}
              </DropdownButton>
            ) : null}

            {showStartDialQueueButton && (
              <Button
                variant='outline-success'
                onClick={handleStartDialQueue}
                disabled={disableStartDialQueue}
                title={
                  !hasActivePolicyDialSession ? intl.formatMessage(lpM.connectHint) : undefined
                }
              >
                <i className='bi bi-telephone-forward me-1'></i>
                {mutatingDialQueue
                  ? intl.formatMessage(lpM.dialStarting)
                  : intl.formatMessage(lpM.dialStart, {count: selectedLeadCount})}
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: Name | Phone | User | Unassigned | Search | → View toggle */}
        <div className='w-100 mb-3 d-flex align-items-center gap-2 flex-wrap'>
          <Form.Control
            name='search'
            type='search'
            value={searchText}
            style={{width: 'calc(220px + 220px + 8px)'}}
            onChange={(e) => {
              setSearchText(e.target.value)
              if (e.target.value === '') {
                setSearchName('')
                setSearchPhone('')
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch(searchText)
            }}
            placeholder={intl.formatMessage(lpM.searchNamePhone)}
          />

          {permissions && canExportLeads && (
            <div style={{width: 'calc(160px + 160px + 8px)'}}>
              <MultiSelectWithActions
                options={userOptions}
                value={selectedUserOptions}
                onChange={(selected: {value: string; label: string}[] | null) => {
                  setValueUsers((selected ?? []).map((option) => option.value))
                  if (selected && selected.length > 0) setFilterUnassigned(false)
                }}
                placeholder={intl.formatMessage(lpM.selectUsers)}
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={{
                  control: (base: Record<string, unknown>) => ({...base, minHeight: '38px'}),
                  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
                }}
              />
            </div>
          )}

          <Button variant='secondary' onClick={() => research()} type='submit'>
            <i className='bi bi-search text-dark' />
          </Button>

          <div className='ms-auto d-flex align-items-center gap-2'>
            {flagView === 'card' && (
              <ButtonGroup className='btn-sm kanban-zoom-controls'>
                <Button
                  variant='secondary'
                  className='btn-sm'
                  disabled={kanbanZoom <= KANBAN_ZOOM_MIN}
                  title={intl.formatMessage(lpM.zoomOut)}
                  onClick={() => changeKanbanZoom(kanbanZoom - KANBAN_ZOOM_STEP)}
                >
                  <i className='bi bi-dash-lg' />
                </Button>
                <Button
                  variant='secondary'
                  className='btn-sm'
                  title={intl.formatMessage(lpM.zoomReset)}
                  onClick={() => changeKanbanZoom(KANBAN_ZOOM_MAX)}
                >
                  {Math.round(kanbanZoom * 100)}%
                </Button>
                <Button
                  variant='secondary'
                  className='btn-sm'
                  disabled={kanbanZoom >= KANBAN_ZOOM_MAX}
                  title={intl.formatMessage(lpM.zoomIn)}
                  onClick={() => changeKanbanZoom(kanbanZoom + KANBAN_ZOOM_STEP)}
                >
                  <i className='bi bi-plus-lg' />
                </Button>
              </ButtonGroup>
            )}
            <ButtonGroup className='btn-sm'>
              <Button
                variant='secondary'
                className='btn-sm'
                active={flagView === 'card'}
                onClick={() => setFlagView('card')}
              >
                <i className='bi bi-postcard' />
              </Button>
              <Button
                variant='secondary'
                className='btn-sm'
                active={flagView === 'list'}
                onClick={() => setFlagView('list')}
              >
                <i className='bi bi-card-list'></i>
              </Button>
            </ButtonGroup>
          </div>
        </div>

        {dialQueue && (
          <Alert
            variant={dialQueue.status === 'PAUSED' ? 'warning' : 'light'}
            className='mb-3 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3'
          >
            <div>
              <div className='d-flex align-items-center gap-2 flex-wrap mb-1'>
                <strong>{intl.formatMessage(lpM.dialTitle)}</strong>
                <Badge
                  bg={
                    dialQueue.status === 'RUNNING'
                      ? 'success'
                      : dialQueue.status === 'PAUSED'
                      ? 'warning'
                      : 'secondary'
                  }
                >
                  {dialQueue.status}
                </Badge>
                {dialQueue.campaign_id && (
                  <Badge bg='light' text='dark' className='border'>
                    {intl.formatMessage(lpM.dialCampaign, {id: dialQueue.campaign_id})}
                  </Badge>
                )}
              </div>
              <div className='small text-muted'>
                {intl.formatMessage(lpM.dialStatsSelected, {
                  completed: dialQueue.counts.completed,
                  total: dialQueue.counts.total,
                })}
                {bullet}
                {intl.formatMessage(lpM.dialStatsPending, {count: dialQueue.counts.pending})}
                {bullet}
                {intl.formatMessage(lpM.dialStatsDialing, {count: dialQueue.counts.dialing})}
                {dialQueue.counts.failed > 0
                  ? intl.formatMessage(lpM.dialStatsFailed, {count: dialQueue.counts.failed})
                  : ''}
              </div>
              {dialQueue.current_item && (
                <div className='small mt-1'>
                  {intl.formatMessage(lpM.dialCurrent)}
                  {colon}
                  {space}
                  <strong>
                    {dialQueue.current_item.lead_name || dialQueue.current_item.phone}
                  </strong>
                  {dialQueue.current_item.phone ? midDot + dialQueue.current_item.phone : ''}
                </div>
              )}
              {dialQueue.next_dial_at &&
                dialQueue.status === 'RUNNING' &&
                !dialQueue.counts.dialing && (
                  <div className='small mt-1 text-muted'>
                    {intl.formatMessage(lpM.dialNextCall, {
                      time: new Date(dialQueue.next_dial_at).toLocaleTimeString(),
                    })}
                  </div>
                )}
              {dialQueue.last_error && (
                <div className='small text-danger mt-1'>{dialQueue.last_error}</div>
              )}
            </div>
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              {loadingDialQueue && <Spinner animation='border' size='sm' />}
              {dialQueue.status === 'RUNNING' ? (
                <Button
                  variant='outline-warning'
                  onClick={handlePauseDialQueue}
                  disabled={mutatingDialQueue}
                >
                  {intl.formatMessage(lpM.dialPause)}
                </Button>
              ) : (
                <Button
                  variant='outline-success'
                  onClick={handleResumeDialQueue}
                  disabled={mutatingDialQueue}
                >
                  {intl.formatMessage(lpM.dialResume)}
                </Button>
              )}
              <Button
                variant='outline-danger'
                onClick={handleStopDialQueue}
                disabled={mutatingDialQueue}
              >
                {intl.formatMessage(lpM.dialStop)}
              </Button>
            </div>
          </Alert>
        )}

        {flagView === 'card' ? (
          <div
            className='d-flex gap-2 container-draggs kanban-zoom'
            style={
              {
                maxWidth: '100%',
                overflowX: 'auto',
                '--kanban-scale': kanbanZoom,
              } as CSSProperties
            }
          >
            <ContentDragDropLeads
              pipelineId={pipelineId}
              pipelines={dataPipelines.allPipelines.edges}
              onOpenEdit={onOpenEdit}
              setInfoLead={setInfoLead}
              updateListLeads={updateListLeads}
              leadPatch={leadPatch}
              flagLeads={flag}
              setLoadingState={(b: boolean) => setIsLoadingChangeState(b)}
              filterStage={filterStage}
              searchName={searchName}
              searchPhone={searchPhone}
              valueUsers={valueUsers}
              filterUnassigned={filterUnassigned}
              datesFrom={dateFrom}
              datesTo={dateTo}
              permissions={permissions}
              isRingIntegration={isRingIntegration}
              stagesDeclined={stagesDeclined}
            />
          </div>
        ) : (
          <div className='lead-list' style={{maxWidth: '100%', overflowX: 'auto'}}>
            <ContentListLeads
              pipelineId={pipelineId}
              onOpenEdit={onOpenEdit}
              setInfoLead={setInfoLead}
              flagLeads={flag}
              updateListLeads={updateListLeads}
              leadPatch={leadPatch}
              setLoadingState={(b: boolean) => setIsLoadingChangeState(b)}
              statePipeline={statePipeline}
              searchName={searchName}
              searchPhone={searchPhone}
              datesFrom={dateFrom}
              datesTo={dateTo}
              valueUsers={valueUsers}
              filterUnassigned={filterUnassigned}
              pageRefresh={pageRefresh}
              isRingIntegration={isRingIntegration}
              stagesDeclined={stagesDeclined}
              leadsSelected={leadsSelected}
              setLeadsSelected={setLeadsSelected}
            />
          </div>
        )}
      </div>

      {isOpenAdd && (
        <DrawerAddLead
          isOpen={isOpenAdd}
          onClose={onCloseAdd}
          updateListLeads={updateListLeads}
          initialPipelineId={pipelineId}
          dataPipelines={dataPipelines}
        />
      )}
      {isOpenEdit && infoLead && (
        <DrawerEditLead
          isOpen={isOpenEdit}
          onClose={handleCloseEditLead}
          info={infoLead}
          updateListLeads={updateListLeads}
          onLeadUpdated={setLeadPatch}
          dataPipelines={dataPipelines}
        />
      )}
      {isOpenReAssign && infoReAssign && (
        <ModalReasingLeads
          isOpen={isOpenReAssign}
          onClose={onCloseReAssign}
          info={infoReAssign}
          updateListLeads={updateListLeads}
          iStagePipeline={stageByPipeline}
          isExport={isExport}
          flagView={flagView}
        />
      )}
      {isOpenImport && pipelineId && (
        <ModalImportLeads
          isOpen={isOpenImport}
          onClose={onCloseImport}
          onImported={updateListLeads}
          pipelineId={pipelineId}
          pipelineName={
            dataPipelines.allPipelines.edges.find(({node}) => node.id === pipelineId)?.node.name || ''
          }
        />
      )}
      {isOpenConfigPipeline && infoConfigPipeline && (
        <ModalEditPipeline
          isOpen={isOpenConfigPipeline}
          onClose={onCloseConfigPipeline}
          refetch={refreshList}
          pipeline={infoConfigPipeline}
        />
      )}
      <LeadTagManager show={showTagManager} onHide={() => setShowTagManager(false)} />
    </>
  )
}
